/*
 * The Cards round: the room picks, then the facilitator decides what the job
 * keeps. From the prototype's draft screens.
 *
 * Two ways to pick, and they are not redundant. The deal shows one card at a
 * time and makes a room read a card it would have scrolled past; the spread lets
 * someone change their mind against the whole deck. The deal is the default
 * because it is the one that produces surprise.
 */

import type { FamilyNumber } from "@/theme/colors"

export const CARDS_COPY = {
  heading: "Explore the opportunities.",
  objective: "Select the cards that best fit the problem.",

  /** The view tabs. */
  dealTab: "Deal",
  spreadTab: "Whole deck",

  /** The deal. "Skip" rather than "Pass": a passed card reads as gone for good,
      but the deck can be run again and the spread shows every card. */
  skip: "Skip",
  keep: "Keep",
  /** Throwing right at a card you already hold takes it back out. */
  dropIt: "Drop it",
  keepFull: "Hand full",
  handFullHint: "Your hand is full. Drop one to swap it out.",
  flipToBack: "Flip",
  flipToFront: "Show the card",
  dragHint: "Drag it, or tap to read the back",
  backACard: "Back a card",
  /** The deal's own footer: how many are picked so far, and how to decide. */
  dealPicked: (held: number, cap: number) =>
    cap > 0 ? `${held} of ${cap} picked` : `${held} picked`,
  dragToDecide: "Drag a card left to skip, right to keep.",
  previousFamily: "Previous family",
  nextFamily: "Next family",
  dealEndTitle: "That's the whole deck.",
  dealEndBody:
    "Your hand is set for this job. Go back through the deck or open the full spread to change your mind.",
  dealAgain: "Run it again",
  toSpread: "See the whole deck",

  /** The spread. */
  take: "Pick",
  drop: "In hand",
  takeFull: "Hand full",
  /** Facilitator's version of the same button: a count, not an action. */
  nominations: (n: number) =>
    n === 0 ? "No nominations" : `${n} ${n === 1 ? "nomination" : "nominations"}`,

  /** The hand. */
  handLabel: "Your hand",
  handCount: (held: number, cap: number) => (cap > 0 ? `${held}/${cap}` : `${held} picked`),
  handUnlimited: "∞",
  capReached: (cap: number) => `That's ${cap}. Drop one to take another.`,
  emptyHand: "Nothing picked yet.",

  /** Facilitator's monitor, while the room picks. */
  monitorTitle: "Who has picked what",
  statePicking: "Picking",
  stateDone: "Done",
  pickedNothing: "Nothing yet",
  hideMonitor: "Hide who has picked what",
  showMonitor: "Show who has picked what",

  /** The shortlist. */
  reviewHeading: "What the room reached for.",
  reviewObjective:
    "Every card anyone picked, counted. The top six are pre-selected. Drop any of them, or pull a lower card in when someone argues for it. What stays is the hand everyone places.",
  reviewOrder: "Every nomination · most picked first",
  /*
   * Named as states rather than verbs, because the shortlist arrives already
   * populated: the facilitator is reading a list and taking things off it, not
   * building one from nothing.
   */
  keepCard: "Bring in",
  dropCard: "In the hand",
  kept: "Kept",
  /** Why six, said once, under the list. */
  reviewFoot:
    "Six is the usual number. More than eight and the fit board stops being a conversation.",
  nominatedBy: (n: number) => `${n} ${n === 1 ? "person" : "people"}`,
  shortlistCount: (kept: number, total: number) => `${kept} of ${total} carrying forward`,
  /*
   * Said plainly because the write behind this button deletes the placements and
   * deep-dive answers of anything dropped. Reversible, but not silently.
   */
  dropWarning: "Dropping a card clears anything the room already said about it.",
  emptyShortlist: "Nobody picked a card yet.",

  /** The trash badge on a card in your hand, wherever the hand is shown. */
  dropFromHand: (name: string) => `Drop ${name} from your hand`,

  imDone: "I'm done",
  imDoneWaiting: "Done, waiting on the facilitator",
} as const

export const DECK_BROWSER_COPY = {
  // The title switches on context the same way the blurb below does: the
  // standalone deck (the /deck page and the public preview) invites you to
  // explore, while mid-session it stays "Browse" to read as a reference.
  titleInSession: "Browse by what AI can do.",
  titleStandalone: "Explore what AI can do.",
  blurbInSession: "Get to know the deck while you wait for others to join.",
  blurbStandalone:
    "Six families of AI capabilities. Click a card to learn more about what it can do.",
  backToLobby: "Back to lobby",
  oneTab: "One at a time",
  deckTab: "Whole deck",
  /*
   * The browser is reachable mid-session, so it has to say out loud that it is
   * not the picking screen — the cards look identical.
   */
  readOnly: "Nothing is picked here. This is just a read of the deck.",
  open: "Flip through the deck",
  whatThisMeans: "What this means",
  close: "Close",
  previous: "Previous card",
  next: "Next card",
  tapHint: "Click a card to learn more",
  singleView: "One at a time",
  gridView: "Whole deck",
  learnMore: "Learn more",
  aboutTitle: "About this capability",
} as const

/**
 * One-line family descriptions for the grid view's section headers.
 *
 * DRAFT — from the handoff's `GDESC`, not yet content-reviewed (the family
 * `blurb` in `content/families` is the reviewed copy used elsewhere; these are
 * the grid-specific, shorter lines the handoff calls for). Keyed by the stable
 * family id, so the handoff's printed order is remapped (Reflects 5→6,
 * Partners 6→7). No family 5: Monitors is retired.
 */
export const GRID_FAMILY_DESC: Partial<Record<FamilyNumber, string>> = {
  1: "Making sense of images, sound and the world around us.",
  2: "Turning ideas into clear text, voice and conversation.",
  3: "Finding information and patterns that lead to better decisions.",
  4: "Doing tasks and running workflows across systems.",
  6: "Explaining its reasoning and helping people think things through.",
  7: "Working alongside people to create and decide, without replacing them.",
}

/**
 * The detail overlay's structural copy (labels and section headings).
 *
 * LAYOUT ONLY — the "Be aware with…" section's per-card body text is DRAFT and
 * lands in a later content PR; until then it shows skeleton placeholders. "See it
 * in action" is live (from `content/patterns`); each pattern shows its name and
 * "use it when" line. Tech stack, the old "What it can look like" examples, the
 * "A good first version" section and the three "What it is / need / goes wrong"
 * sections are intentionally gone from this design.
 */
export const DECK_DETAIL_COPY = {
  /** The single hero section; copy follows in a later PR. */
  beAware: "Be aware with…",
  inAction: "See it in action",
  patternsComingSoon: "Patterns for this card are coming soon",
  /** The demo stage's controls (see `PatternDemo`). */
  replay: "Replay",
  tryIt: "Try it",
  close: "Close",
} as const
